import { useMemo, useState } from 'react';
import { Bike, Inbox, Store as StoreIcon } from 'lucide-react';
import { type ColumnDef } from '@tanstack/react-table';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { ErrorState } from '@/components/ui/ErrorState';
import { EmptyState } from '@/components/ui/EmptyState';
import { LineChart } from '@/components/charts/LineChart';
import {
  ChartModeToggle,
  type ChartGranularity,
  type SeasonalityMode,
} from '@/components/charts/ChartModeToggle';
import {
  bucketBySeasonality,
  disabledGranularitiesFor,
  granularityForSeasonality,
} from '@/components/charts/seasonality';
import { mergeBucketedPrevious, mergeIndexedPrevious } from '@/components/charts/compareSeries';
import { AnimatedNumber } from '@/components/charts/AnimatedNumber';
import { DataView, downloadCsv } from '@/components/data/DataView';
import { compareRangeFor, useFilters, rangeISO } from '@/state/filters';
import {
  useOrdersTimeseries,
  useRiderUtilization,
  useStoreComparison,
} from '@/api/bi';
import type { RiderRow, StoreComparisonRow } from '@/api/bi/types';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/cn';
import { openRiderOrders } from '@/state/riderOrdersDialog';

/**
 * Strategic — long-arc network health.
 *
 * Stage 1 polish:
 *   - GMV trend KPIs animate; the chart stays as-is (already healthy).
 *   - Store comparison + rider tables migrate to `<DataView>`: sortable
 *     columns, search box, CSV export. Embedded inside Cards via the
 *     `density="compact"` flag so the existing card chrome stays.
 *   - Store comparison keeps the inline GMV bar — it's the primary signal —
 *     by rendering it inside the cell.
 */
export function StrategicPage() {
  const { range, storeId, compareMode, timeRangeMode } = useFilters();
  const iso = useMemo(() => rangeISO(range, timeRangeMode), [range, timeRangeMode]);
  const compareEnabled = compareMode !== 'none';
  const compareRange = useMemo(
    () => compareRangeFor(range, compareMode, timeRangeMode),
    [range, compareMode, timeRangeMode],
  );
  const filters = { start: iso.start, end: iso.end, storeId };

  // Strategic was previously hard-coded to `day`; surface the same
  // Granularity/Seasonality controls Overview now has so analysts can
  // jump between chronological trend and "what does Friday look like?".
  const [granularity, setGranularity] = useState<ChartGranularity>('day');
  const [seasonality, setSeasonality] = useState<SeasonalityMode>('none');
  const disabledGranularities = useMemo(
    () => disabledGranularitiesFor(range.from, range.to),
    [range.from, range.to],
  );
  // Seasonality views need a specific server bucketing (hour-of-day → hour,
  // day-of-week → day) — otherwise the client-side roll-up has nothing
  // useful to fold and the chart silently empties out.
  const requiredGranularity = granularityForSeasonality(seasonality);
  const effectiveGranularity: ChartGranularity = requiredGranularity ?? granularity;

  const trend = useOrdersTimeseries({ ...filters, granularity: effectiveGranularity });
  const previousTrend = useOrdersTimeseries({
    start: compareRange?.start ?? '',
    end: compareRange?.end ?? '',
    storeId,
    granularity: effectiveGranularity,
    enabled: compareEnabled && Boolean(compareRange),
  });
  const stores = useStoreComparison({ start: iso.start, end: iso.end });
  const riders = useRiderUtilization(filters);

  const trendData = useMemo(
    () =>
      (trend.data?.buckets ?? []).map((b) => ({
        t: b.t,
        gmv: b.gmv,
        orders: b.orders,
      })),
    [trend.data],
  );
  const compareTrendData = useMemo(
    () =>
      (previousTrend.data?.buckets ?? []).map((b) => ({
        t: b.t,
        gmv: b.gmv,
        orders: b.orders,
      })),
    [previousTrend.data],
  );
  const chartTrendData = useMemo(
    () => mergeIndexedPrevious(trendData, compareTrendData, ['gmv'], compareEnabled),
    [trendData, compareTrendData, compareEnabled],
  );

  const seasonalityData = useMemo(() => {
    if (seasonality === 'none') return [] as { bucket: string; gmv: number }[];
    const current = bucketBySeasonality(trendData, {
      timeKey: 't',
      metrics: ['gmv'],
      mode: seasonality,
      aggregate: 'sum',
    }).map((r) => ({ bucket: String(r.bucket), gmv: Number(r.gmv) }));
    const previous = bucketBySeasonality(compareTrendData, {
      timeKey: 't',
      metrics: ['gmv'],
      mode: seasonality,
      aggregate: 'sum',
    }).map((r) => ({ bucket: String(r.bucket), gmv: Number(r.gmv) }));
    return mergeBucketedPrevious(current, previous, ['gmv'], compareEnabled);
  }, [trendData, compareTrendData, seasonality, compareEnabled]);

  const storeRows = stores.data?.stores ?? [];
  const riderRows = riders.data?.riders ?? [];

  const totals = useMemo(() => {
    const gmv = storeRows.reduce((s, r) => s + r.gmv, 0);
    const orders = storeRows.reduce((s, r) => s + r.orders, 0);
    const completed = storeRows.reduce((s, r) => s + r.completed, 0);
    const cancelled = storeRows.reduce((s, r) => s + r.cancelled, 0);
    return { gmv, orders, completed, cancelled, cancelRate: orders > 0 ? cancelled / orders : 0 };
  }, [storeRows]);

  return (
    <>
      <PageHeader
        eyebrow="Strategic"
        title="Network Health"
        description="Long-arc trend, store contribution and rider utilization across the network."
      />

      <div className="grid gap-4 stagger sm:grid-cols-2 lg:grid-cols-4">
        <NetworkStat
          label="Network GMV"
          value={totals.gmv}
          format={(n) => fmt.npr(n)}
          loading={stores.isLoading}
          hint="Sum of GMV across all stores in this window."
        />
        <NetworkStat
          label="Orders"
          value={totals.orders}
          format={(n) => fmt.compact(n)}
          loading={stores.isLoading}
          hint="All orders placed across the network."
        />
        <NetworkStat
          label="Completed"
          value={totals.completed}
          format={(n) => fmt.compact(n)}
          loading={stores.isLoading}
          hint="Orders that reached the completed state."
        />
        <NetworkStat
          label="Network cancel"
          value={totals.cancelRate}
          format={(n) => fmt.pct(n)}
          loading={stores.isLoading}
          tone={totals.cancelRate > 0.05 ? 'danger' : 'success'}
          hint="Cancelled ÷ total across the network."
        />
      </div>

      <Card className="mt-4">
        <CardHeader>
          <CardTitle>GMV trend</CardTitle>
          <ChartModeToggle
            granularity={granularity}
            onGranularityChange={setGranularity}
            disabledGranularities={disabledGranularities}
            seasonality={seasonality}
            onSeasonalityChange={setSeasonality}
          />
        </CardHeader>
        <CardBody>
          {trend.isLoading || (compareEnabled && previousTrend.isLoading) ? (
            <Skeleton className="h-64 w-full" />
          ) : trend.isError ? (
            <ErrorState onRetry={() => void trend.refetch()} />
          ) : trendData.length === 0 ? (
            <EmptyState icon={<Inbox className="h-5 w-5" />} title="No trend data" withTip />
          ) : seasonality !== 'none' ? (
            <LineChart
              data={seasonalityData}
              xKey="bucket"
              series={[
                { key: 'gmv', label: 'GMV' },
                ...(compareEnabled
                  ? [
                      {
                        key: 'gmv_prev',
                        label: 'GMV prev',
                        color: 'hsl(var(--primary) / 0.55)',
                        strokeDasharray: '4 4',
                      },
                    ]
                  : []),
              ]}
              formatX={(s) => s}
              formatValue={(v) => fmt.npr(v)}
              height={260}
            />
          ) : (
            <LineChart
              data={chartTrendData}
              xKey="t"
              series={[
                { key: 'gmv', label: 'GMV' },
                ...(compareEnabled
                  ? [
                      {
                        key: 'gmv_prev',
                        label: 'GMV prev',
                        color: 'hsl(var(--primary) / 0.55)',
                        strokeDasharray: '4 4',
                      },
                    ]
                  : []),
              ]}
              formatValue={(v) => fmt.npr(v)}
              height={260}
            />
          )}
        </CardBody>
      </Card>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <StoreComparisonTable
          rows={storeRows}
          loading={stores.isLoading}
          isError={stores.isError}
          onRetry={() => void stores.refetch()}
        />
        <RiderUtilizationTable
          rows={riderRows}
          loading={riders.isLoading}
          isError={riders.isError}
          onRetry={() => void riders.refetch()}
        />
      </div>
    </>
  );
}

interface NetworkStatProps {
  label: string;
  value: number | null;
  format: (n: number) => string;
  loading?: boolean;
  hint?: string;
  tone?: 'success' | 'danger';
}

function NetworkStat({ label, value, format, loading, hint, tone }: NetworkStatProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{label}</CardTitle>
      </CardHeader>
      <CardBody>
        {loading ? (
          <Skeleton className="h-8 w-24" />
        ) : (
          <p
            className={cn(
              'font-display text-2xl font-bold tracking-tight kpi-num sm:text-3xl',
              tone === 'success' && 'text-success',
              tone === 'danger' && 'text-danger',
            )}
          >
            <AnimatedNumber value={value} format={format} duration={500} />
          </p>
        )}
        {hint ? <p className="mt-1 text-xs text-muted-foreground">{hint}</p> : null}
      </CardBody>
    </Card>
  );
}

function StoreComparisonTable({
  rows,
  loading,
  isError,
  onRetry,
}: {
  rows: StoreComparisonRow[];
  loading: boolean;
  isError: boolean;
  onRetry: () => void;
}) {
  const [search, setSearch] = useState('');
  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) => r.store_name.toLowerCase().includes(needle));
  }, [rows, search]);
  const maxGmv = useMemo(() => Math.max(1, ...rows.map((r) => r.gmv ?? 0)), [rows]);

  const columns = useMemo<ColumnDef<StoreComparisonRow>[]>(
    () => [
      {
        accessorKey: 'store_name',
        header: 'Store',
        cell: ({ row }) => {
          const r = row.original;
          const ratio = r.gmv / maxGmv;
          return (
            <div className="min-w-[180px]">
              <p className="truncate font-medium">{r.store_name}</p>
              <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full bg-primary"
                  style={{
                    width: `${Math.max(2, Math.round(ratio * 100))}%`,
                    transition: 'width 500ms var(--ease-out)',
                  }}
                />
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: 'gmv',
        header: 'GMV',
        cell: ({ getValue }) => (
          <span className="kpi-num font-semibold">{fmt.npr(getValue<number>())}</span>
        ),
        sortingFn: 'basic',
      },
      {
        accessorKey: 'orders',
        header: 'Orders',
        cell: ({ getValue }) => <span className="kpi-num">{fmt.number(getValue<number>())}</span>,
        sortingFn: 'basic',
      },
      {
        accessorKey: 'aov',
        header: 'AOV',
        cell: ({ getValue }) => <span className="kpi-num text-muted-foreground">{fmt.npr(getValue<number>())}</span>,
        sortingFn: 'basic',
      },
      {
        accessorKey: 'cancel_rate',
        header: 'Cancel',
        cell: ({ getValue }) => {
          const v = getValue<number>();
          return (
            <span
              className={cn(
                'kpi-num',
                v > 0.05 && 'text-danger',
                v <= 0.02 && 'text-success',
              )}
            >
              {fmt.pct(v)}
            </span>
          );
        },
        sortingFn: 'basic',
      },
    ],
    [maxGmv],
  );

  return (
    <DataView
      title="Store comparison · GMV"
      description="Sorted by GMV by default — click any column header to re-sort."
      data={filtered}
      columns={columns}
      isLoading={loading}
      isError={isError}
      onRetry={onRetry}
      density="compact"
      emptyTitle="No store activity"
      emptyDescription="Try widening the date range or selecting a different store."
      search={{ value: search, onChange: setSearch, placeholder: 'Search stores…' }}
      onExportCsv={() =>
        downloadCsv(
          `stores-${new Date().toISOString().slice(0, 10)}.csv`,
          rows.map((r) => ({
            store_id: r.store_id,
            store_name: r.store_name,
            orders: r.orders,
            completed: r.completed,
            cancelled: r.cancelled,
            gmv: r.gmv,
            aov: r.aov,
            cancel_rate: r.cancel_rate,
          })),
          [
            { key: 'store_id', label: 'store_id' },
            { key: 'store_name', label: 'store' },
            { key: 'orders', label: 'orders' },
            { key: 'completed', label: 'completed' },
            { key: 'cancelled', label: 'cancelled' },
            { key: 'gmv', label: 'gmv' },
            { key: 'aov', label: 'aov' },
            { key: 'cancel_rate', label: 'cancel_rate' },
          ],
        )
      }
      toolbar={<StoreIcon className="h-4 w-4 text-muted-foreground" aria-hidden />}
    />
  );
}

function RiderUtilizationTable({
  rows,
  loading,
  isError,
  onRetry,
}: {
  rows: RiderRow[];
  loading: boolean;
  isError: boolean;
  onRetry: () => void;
}) {
  const [search, setSearch] = useState('');
  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) => r.rider_name.toLowerCase().includes(needle));
  }, [rows, search]);
  const maxGmv = useMemo(() => Math.max(1, ...rows.map((r) => r.gmv ?? 0)), [rows]);

  const columns = useMemo<ColumnDef<RiderRow>[]>(
    () => [
      {
        accessorKey: 'rider_name',
        header: 'Rider',
        cell: ({ row }) => {
          const r = row.original;
          return (
            <div className="min-w-[160px]">
              <p className="truncate font-medium">{r.rider_name}</p>
              <div className="mt-1 h-1 overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full bg-primary"
                  style={{
                    width: `${Math.max(2, Math.round((r.gmv / maxGmv) * 100))}%`,
                    transition: 'width 500ms var(--ease-out)',
                  }}
                />
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: 'deliveries',
        header: 'Deliveries',
        cell: ({ getValue }) => <span className="kpi-num">{fmt.number(getValue<number>())}</span>,
        sortingFn: 'basic',
      },
      {
        accessorKey: 'gmv',
        header: 'GMV',
        cell: ({ getValue }) => <span className="kpi-num">{fmt.npr(getValue<number>())}</span>,
        sortingFn: 'basic',
      },
      {
        accessorKey: 'gmv_per_delivery',
        header: 'Per delivery',
        cell: ({ getValue }) => (
          <span className="kpi-num text-muted-foreground">{fmt.npr(getValue<number>())}</span>
        ),
        sortingFn: 'basic',
      },
    ],
    [maxGmv],
  );

  return (
    <DataView
      title="Rider utilization"
      description="Top riders by GMV — sortable. Use search to find one quickly."
      data={filtered}
      columns={columns}
      isLoading={loading}
      isError={isError}
      onRetry={onRetry}
      density="compact"
      emptyTitle="No rider activity"
      emptyDescription="Try widening the date range — riders show up once they complete a delivery."
      search={{ value: search, onChange: setSearch, placeholder: 'Search riders…' }}
      onRowClick={(r) =>
        openRiderOrders({
          riderId: r.rider_id,
          riderName: r.rider_name,
        })
      }
      onExportCsv={() =>
        downloadCsv(
          `riders-${new Date().toISOString().slice(0, 10)}.csv`,
          rows.map((r) => ({
            rider_id: r.rider_id,
            rider_name: r.rider_name,
            deliveries: r.deliveries,
            gmv: r.gmv,
            gmv_per_delivery: r.gmv_per_delivery,
          })),
          [
            { key: 'rider_id', label: 'rider_id' },
            { key: 'rider_name', label: 'rider' },
            { key: 'deliveries', label: 'deliveries' },
            { key: 'gmv', label: 'gmv' },
            { key: 'gmv_per_delivery', label: 'gmv_per_delivery' },
          ],
        )
      }
      toolbar={<Bike className="h-4 w-4 text-muted-foreground" aria-hidden />}
    />
  );
}
